import React from 'react'
import { createColumnHelper } from '@tanstack/react-table'

import { FormattedCurrency, FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_SIZE_SMALL, BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import { createTagColumn, createTextColumn } from '@divvy-web/skylab.datagrid'

import { CreditApplicationDashboardStatus } from '@sputnik/pages/DashboardPages/types'
import { formatDate, getDashboardStatusTagProps, getDashboardStatusValue } from '@sputnik/pages/DashboardPages/utils'

import { GetCreditApplications_creditApplications_CreditApplication } from './GetCreditApplications.gql'
import styles from './styles.module.scss'

const columnHelper = createColumnHelper<GetCreditApplications_creditApplications_CreditApplication>()

// TODO: figure out if this mapping contains all statuses that need mapped

interface ColumnConfigurationParams {
  onViewDetailsClick: (salesforceCreditId: string) => void
}

export const getColumnConfiguration = ({ onViewDetailsClick }: ColumnConfigurationParams) => [
  createTextColumn(columnHelper, 'businessInfo.legalBusinessName', {
    id: 'business-name',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Company name'
        id='sputnik.dataGrid__FPGwAt'
      />
    ),
  }),
  createTagColumn(columnHelper, 'status', {
    id: 'status',
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const statusA = getDashboardStatusValue(rowA?.original.status)
      const statusB = getDashboardStatusValue(rowB?.original.status)

      if (statusA < statusB) {
        return -1
      } else if (statusA > statusB) {
        return 1
      }
      return 0
    },
    header: () => (
      <FormattedMessage
        defaultMessage='Status'
        id='sputnik.dataGrid__tzMNF3'
      />
    ),
    options: {
      [CreditApplicationDashboardStatus.InReview]: getDashboardStatusTagProps(
        CreditApplicationDashboardStatus.InReview,
      ),
      [CreditApplicationDashboardStatus.Incomplete]: getDashboardStatusTagProps(
        CreditApplicationDashboardStatus.Incomplete,
      ),
      [CreditApplicationDashboardStatus.Expired]: getDashboardStatusTagProps(CreditApplicationDashboardStatus.Expired),
      [CreditApplicationDashboardStatus.ActionNeeded]: getDashboardStatusTagProps(
        CreditApplicationDashboardStatus.ActionNeeded,
      ),
      [CreditApplicationDashboardStatus.AccountCreated]: getDashboardStatusTagProps(
        CreditApplicationDashboardStatus.AccountCreated,
      ),
      [CreditApplicationDashboardStatus.Declined]: getDashboardStatusTagProps(
        CreditApplicationDashboardStatus.Declined,
      ),
    },
    getValueKey: (row) => getDashboardStatusValue(row?.original.status),
  }),
  createTextColumn(columnHelper, 'startedAt', {
    id: 'started-at',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Started on'
        id='sputnik.dataGrid__Z5cU8c'
      />
    ),
    getTextValue: (row) => formatDate(row?.original.startedAt),
  }),
  createTextColumn(columnHelper, 'shouldBeExpiredAt', {
    id: 'expires-at',
    enableSorting: true,
    header: () => (
      <FormattedMessage
        defaultMessage='Expires on'
        id='sputnik.dataGrid__0t4WRs'
      />
    ),
    getTextValue: (row) => formatDate(row?.original.shouldBeExpiredAt),
  }),
  createTextColumn(columnHelper, 'fundableLimit', {
    id: 'line-of-credit',
    enableSorting: true,
    header: () => (
      <div className={styles['line-of-credit-column-header']}>
        <FormattedMessage
          defaultMessage='Line of credit'
          id='sputnik.dataGrid__uYh9fa'
        />
      </div>
    ),
    cell: (row) => {
      return (
        <div className={styles['line-of-credit-column']}>
          {!row.getValue() ? '—' : <FormattedCurrency value={row.getValue() || 0} />}
        </div>
      )
    },
  }),
  columnHelper.display({
    id: 'action',
    header: () => (
      <div className={styles['action-column-header']}>
        <FormattedMessage
          defaultMessage='Action'
          id='sputnik.columns__QlsDcr'
        />
      </div>
    ),
    cell: (info) => (
      <div className={styles['action-column']}>
        <BasicButton
          size={BASIC_BUTTON_SIZE_SMALL}
          type={BASIC_BUTTON_TYPE_FLAT}
          onClick={() => {
            onViewDetailsClick(info.row.original.salesforceCreditId)
          }}
        >
          <FormattedMessage
            defaultMessage='View details'
            id='sputnik.columns__MnpUD7'
          />
        </BasicButton>
      </div>
    ),
  }),
]
